import { useCallback, useContext, useEffect, useMemo } from 'react'

import { CoreContext } from 'context/CoreContext'
import {
    MarkAllNotificationsRead,
    ReadNotifications,
    UpdateNotification,
} from 'services/notifications'
import { apiRequest } from 'utils'
import { normalizeNotifications } from 'utils/notifications'

export default function useNotificationCenter({ enabled = true, autoLoad = false } = {}) {
    const {
        notifications,
        setNotifications,
        notificationsLoading,
        setNotificationsLoading,
        notificationsLoaded,
        setNotificationsLoaded,
        notificationsError,
        setNotificationsError,
        notificationsUserIdRef,
        language,
        user,
    } = useContext(CoreContext)

    const loadNotifications = useCallback(async () => {
        if (!enabled || !user?.id) {
            setNotifications([])
            setNotificationsLoaded(false)
            setNotificationsError(false)
            if (notificationsUserIdRef) notificationsUserIdRef.current = null
            return []
        }

        if (notificationsUserIdRef?.current && `${notificationsUserIdRef.current}` !== `${user.id}`) {
            setNotifications([])
        }
        if (notificationsUserIdRef) notificationsUserIdRef.current = user.id

        setNotificationsLoading(true)
        setNotificationsError(false)

        const result = await apiRequest(() => ReadNotifications())
        if (result === false || result?.error) {
            setNotificationsLoading(false)
            setNotificationsLoaded(true)
            setNotificationsError(true)
            return null
        }

        const nextNotifications = normalizeNotifications(Array.isArray(result) ? result : result?.data, language)
        setNotifications(nextNotifications)
        setNotificationsLoading(false)
        setNotificationsLoaded(true)
        return nextNotifications
    }, [enabled, language, notificationsUserIdRef, setNotifications, setNotificationsError, setNotificationsLoaded, setNotificationsLoading, user?.id])

    const markNotificationRead = useCallback(async notification => {
        if (!notification?.id || !notification?.unread) return notification

        const readAt = new Date().toISOString()
        const previousReadAt = notification.readAt
        setNotifications(current => current.map(item => (
            `${item.id}` === `${notification.id}` ? { ...item, readAt, read_at: readAt, unread: false } : item
        )))

        const result = await apiRequest(() => UpdateNotification({ read_at: readAt }, notification.id))
        if (result === false || result?.error) {
            setNotifications(current => current.map(item => (
                `${item.id}` === `${notification.id}`
                    ? { ...item, readAt: previousReadAt, read_at: previousReadAt, unread: !previousReadAt }
                    : item
            )))
            return notification
        }

        return { ...notification, readAt, read_at: readAt, unread: false }
    }, [setNotifications])

    const markAllRead = useCallback(async () => {
        if (!notifications.some(item => item.unread)) return true

        const previousNotifications = notifications
        const readAt = new Date().toISOString()
        setNotifications(current => current.map(item => ({
            ...item,
            readAt: item.readAt || readAt,
            read_at: item.readAt || readAt,
            unread: false,
        })))

        const result = await apiRequest(() => MarkAllNotificationsRead())
        if (result === false || result?.error) {
            const reloadedNotifications = await loadNotifications()
            if (reloadedNotifications === null) {
                setNotifications(previousNotifications)
            }
            return false
        }

        return true
    }, [loadNotifications, notifications, setNotifications])

    useEffect(() => {
        if (autoLoad) {
            loadNotifications()
        }
    }, [autoLoad, loadNotifications])

    return {
        notifications,
        unreadCount: useMemo(() => notifications.filter(item => item.unread).length, [notifications]),
        loading: notificationsLoading,
        loaded: notificationsLoaded,
        error: notificationsError,
        loadNotifications,
        markNotificationRead,
        markAllRead,
    }
}
